import { useMemo, useState } from "react"
import dayjs from "dayjs"
import { toast } from "sonner"

import BackIcon from "@/components/icons/back-icon"
import UsageDayChart from "./usage-day-chart"

import { DETAIL_TEXT, WEEKDAYS } from "../usage-const"
import { formatHourLabels, getDayBackgroundColor } from "../../../units-util"

const UsageMonthDetail = ({
  date,
  onBack,
  dailyData,
  maxVal,
  series,
  rawXLabels,
  unit,
  lang,
  resourceLabel,
  resourceColor,
  endedAt
}) => {
  const [selectedDay, setSelectedDay] = useState(null)

  const year = date?.getFullYear() ?? 0
  const month = date ? date.getMonth() + 1 : 0

  const { daysArray, emptyDays } = useMemo(() => {
    if (!date) return { daysArray: [], emptyDays: [] }

    const daysInMonth = new Date(year, month, 0).getDate()
    const startDay = new Date(year, month - 1, 1).getDay()

    return {
      daysArray: Array.from({ length: daysInMonth }, (_, index) => index + 1),
      emptyDays: Array.from({ length: startDay }, (_, index) => index)
    }
  }, [date, month, year])

  const { filteredLabels, filteredSeries } = useMemo(() => {
    if (!selectedDay || !date) {
      return {
        filteredLabels: [],
        filteredSeries: []
      }
    }

    const indexes = []

    rawXLabels.forEach((label, index) => {
      if (label.startsWith(selectedDay)) {
        indexes.push(index)
      }
    })

    if (!indexes.length) {
      return {
        filteredLabels: [],
        filteredSeries: []
      }
    }

    const start = indexes[0]
    const end = indexes[indexes.length - 1] + 1

    return {
      filteredLabels: formatHourLabels(rawXLabels.slice(start, end)),
      filteredSeries: series.map((item) => ({
        ...item,
        data: item.data.slice(start, end)
      }))
    }
  }, [date, rawXLabels, selectedDay, series])

  if (!date) return null

  const headerDateText = lang === "ko" ? `${year}년 ${month}월` : dayjs(date).format("MMMM YYYY")

  const chartTitle =
    selectedDay === null
      ? null
      : lang === "ko"
        ? `${dayjs(selectedDay).format("MM월 DD일")} 시간별 ${resourceLabel} 사용량`
        : `${resourceLabel} Usage on ${dayjs(selectedDay).format("MMMM D")}`

  const dayClick = (dateCheck, dataCheck, value) => {
    if (dateCheck && dataCheck) {
      setSelectedDay(value)
      return
    }

    if (dateCheck) {
      toast.warning("해당 일에는 사용량이 없습니다.")
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-full p-1 text-gray-700 transition-colors hover:bg-gray-100"
          aria-label={DETAIL_TEXT.back[lang]}
        >
          <BackIcon />
        </button>

        <h4 className="text-xl font-bold text-gray-800">{headerDateText}</h4>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-2 grid grid-cols-7 border-b border-gray-100 pb-2">
          {WEEKDAYS[lang].map((day, index) => (
            <div
              key={day}
              className={`text-center text-xs font-semibold ${
                index === 0 ? "text-red-400" : index === 6 ? "text-blue-400" : "text-gray-400"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {emptyDays.map((value) => (
            <div key={`empty-${value}`} />
          ))}

          {daysArray.map((day) => {
            const currentDate = dayjs(`${year}-${month}-${day}`)
            const dateKey = currentDate.format("YYYY-MM-DD")

            const isBeforeAndSameDate =
              dayjs(currentDate).isBefore(dayjs(endedAt), "day") ||
              dayjs(currentDate).isSame(dayjs(endedAt), "day")

            const value = dailyData?.[dateKey] || 0
            const hasData = value > 0
            const isSelected = selectedDay === day
            const ratio = maxVal > 0 && hasData ? value / maxVal : 0

            const bgColor = getDayBackgroundColor(
              hasData,
              isBeforeAndSameDate,
              ratio,
              resourceColor
            )

            return (
              <button
                key={day}
                type="button"
                onClick={() => dayClick(isBeforeAndSameDate, hasData, dateKey)}
                className={`aspect-square rounded-lg transition-all ${
                  isSelected ? "ring-2 scale-105" : ""
                } ${isBeforeAndSameDate ? "cursor-pointer" : "cursor-default opacity-60"}`}
                style={{
                  backgroundColor: bgColor,
                  boxShadow: isSelected ? `0 0 0 2px ${resourceColor}` : undefined
                }}
              >
                <div
                  className={`pt-2 text-center text-xs font-bold ${
                    isBeforeAndSameDate ? "text-white" : "text-gray-400"
                  }`}
                >
                  {day}
                </div>

                {isBeforeAndSameDate && (
                  <div className="mt-0.5 text-center text-[9px] font-medium text-white/90">
                    {Math.round(ratio * 100)}%
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {selectedDay ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h5 className="mb-3 text-lg font-bold text-gray-800">{chartTitle}</h5>

          <div className="h-64 w-full">
            <UsageDayChart
              labels={filteredLabels}
              series={filteredSeries}
              unit={unit}
              lang={lang}
            />
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-blue-50 p-6 text-center text-blue-800">
          <p className="font-semibold">{DETAIL_TEXT.selectDate[lang]}</p>

          <p className="mt-1 text-sm text-blue-600">{DETAIL_TEXT.selectDesc[lang]}</p>
        </div>
      )}
    </div>
  )
}

export default UsageMonthDetail
